import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, ChevronDown, Ban, Download } from 'lucide-react';
import { supabase, invokeFunction } from '@/lib/supabase';
import type {
  Subscription,
  SubscriptionStatus,
  Profile,
  Order,
  OrderStatus,
  DeliveryLog,
} from '@ellie/shared';
import { Pagination } from '@/components/Pagination';
import { toCsv, downloadCsv } from '@/lib/csv';

const PAGE_SIZE = 25;

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<SubscriptionStatus, string> = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-orange-100 text-orange-700',
  canceled: 'bg-red-100 text-red-700',
  paused: 'bg-gray-100 text-gray-500',
};

const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  preview: 'bg-indigo-100 text-indigo-700',
  pending: 'bg-gray-100 text-gray-700',
  paid: 'bg-blue-100 text-blue-700',
  generating: 'bg-purple-100 text-purple-700',
  review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  delivering: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-500',
};

/** Statuses that can be canceled from the admin panel. */
const CANCELABLE_STATUSES: SubscriptionStatus[] = ['active', 'trialing', 'past_due'];

// =============================================================================
// Types
// =============================================================================

interface SubscriptionWithProfile extends Subscription {
  profiles: Pick<Profile, 'full_name' | 'email'> | null;
}

interface OrderWithDelivery extends Order {
  delivery_log: Pick<DeliveryLog, 'status' | 'type' | 'created_at'>[];
}

// =============================================================================
// Helpers
// =============================================================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatCurrency(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// =============================================================================
// Main Component
// =============================================================================

export function SubscribersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? '1');

  const [subscriptions, setSubscriptions] = useState<SubscriptionWithProfile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Expandable rows
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [ordersCache, setOrdersCache] = useState<Record<string, OrderWithDelivery[]>>({});
  const [loadingOrders, setLoadingOrders] = useState<Set<string>>(new Set());

  // Cancel action
  const [cancelingIds, setCancelingIds] = useState<Set<string>>(new Set());
  const [cancelModalSub, setCancelModalSub] = useState<SubscriptionWithProfile | null>(null);
  const [cancelImmediate, setCancelImmediate] = useState(false);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);

    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    const { data, count, error } = await supabase
      .from('subscriptions')
      .select('*, profiles(full_name, email)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      console.error('Failed to fetch subscriptions:', error.message);
    }

    setSubscriptions((data as SubscriptionWithProfile[]) ?? []);
    setTotalCount(count ?? 0);
    setLoading(false);
  }, [page]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  function handlePageChange(newPage: number) {
    if (newPage > 1) {
      setSearchParams({ page: String(newPage) });
    } else {
      setSearchParams({});
    }
  }

  // ── Expand / collapse rows ────────────────────────────────────────────

  async function handleToggleExpand(subId: string) {
    if (expandedId === subId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(subId);

    // Already cached — no need to re-fetch
    if (ordersCache[subId]) return;

    setLoadingOrders((prev) => new Set(prev).add(subId));

    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, delivery_log(status, type, created_at)')
        .eq('subscription_id', subId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      setOrdersCache((prev) => ({
        ...prev,
        [subId]: (data as OrderWithDelivery[]) ?? [],
      }));
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      alert(`Failed to load orders: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoadingOrders((prev) => {
        const next = new Set(prev);
        next.delete(subId);
        return next;
      });
    }
  }

  // ── Cancel subscription ───────────────────────────────────────────────

  function openCancelModal(sub: SubscriptionWithProfile) {
    setCancelModalSub(sub);
    setCancelImmediate(false);
  }

  async function handleCancelConfirm() {
    const sub = cancelModalSub;
    if (!sub) return;

    // Close modal immediately
    setCancelModalSub(null);

    setCancelingIds((prev) => new Set(prev).add(sub.id));

    try {
      const { data, error: fnError } = await invokeFunction<{ error?: string }>(
        'manage-subscription',
        {
          body: {
            subscription_id: sub.id,
            action: 'cancel',
            immediate: cancelImmediate,
          },
        },
      );

      if (fnError) {
        throw new Error(fnError.message ?? 'Request failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Optimistic update
      setSubscriptions((prev) =>
        prev.map((s) => {
          if (s.id !== sub.id) return s;
          return cancelImmediate
            ? { ...s, status: 'canceled' as SubscriptionStatus, cancel_at_period_end: false }
            : { ...s, cancel_at_period_end: true };
        }),
      );
    } catch (err) {
      console.error('Cancel failed:', err);
      alert(
        `Failed to cancel subscription: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setCancelingIds((prev) => {
        const next = new Set(prev);
        next.delete(sub.id);
        return next;
      });
    }
  }

  function handleExportCsv() {
    const csv = toCsv(subscriptions, [
      { header: 'Name', accessor: (s) => s.profiles?.full_name ?? '' },
      { header: 'Email', accessor: (s) => s.profiles?.email ?? '' },
      { header: 'Pace', accessor: (s) => s.pace },
      { header: 'Status', accessor: (s) => s.status },
      {
        header: 'Canceling',
        accessor: (s) => (s.cancel_at_period_end ? 'Yes' : 'No'),
      },
      { header: 'Renewal Date', accessor: (s) => s.current_period_end ?? '' },
      { header: 'Created', accessor: (s) => s.created_at },
    ]);

    downloadCsv(csv, 'subscribers');
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl text-text-primary">Subscribers</h1>
        <button
          onClick={handleExportCsv}
          disabled={subscriptions.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
        </div>
      ) : subscriptions.length === 0 ? (
        <p className="py-12 text-center text-sm text-text-muted">No subscriptions found.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-gray-50 text-xs font-medium uppercase tracking-wider text-text-muted">
                <th className="w-8 px-2 py-3" />
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Pace</th>
                <th className="px-4 py-3">Renewal</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => {
                const isExpanded = expandedId === sub.id;
                const orders = ordersCache[sub.id];
                const isLoadingOrders = loadingOrders.has(sub.id);
                const canCancel =
                  CANCELABLE_STATUSES.includes(sub.status) && !sub.cancel_at_period_end;

                return (
                  <SubscriptionRow
                    key={sub.id}
                    sub={sub}
                    isExpanded={isExpanded}
                    orders={orders}
                    isLoadingOrders={isLoadingOrders}
                    canCancel={canCancel}
                    isCanceling={cancelingIds.has(sub.id)}
                    onToggleExpand={() => handleToggleExpand(sub.id)}
                    onCancel={() => openCancelModal(sub)}
                  />
                );
              })}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancelModalSub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-text-primary">Cancel Subscription</h2>
            <p className="mb-4 text-sm text-text-muted">
              This will cancel the subscription via Stripe.
            </p>

            {/* Subscription details */}
            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-text-muted">Customer</span>
                <span className="text-text-primary">
                  {cancelModalSub.profiles?.full_name ?? 'Unknown'}
                </span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Email</span>
                <span className="text-text-primary">{cancelModalSub.profiles?.email ?? '-'}</span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Pace</span>
                <span className="capitalize text-text-primary">{cancelModalSub.pace}</span>
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Status</span>
                <SubscriptionStatusBadge status={cancelModalSub.status} />
              </div>
              <div className="mt-1 flex justify-between">
                <span className="text-text-muted">Renewal</span>
                <span className="text-text-primary">
                  {formatDate(cancelModalSub.current_period_end)}
                </span>
              </div>
            </div>

            {/* Immediate toggle */}
            <label className="mb-2 flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={cancelImmediate}
                onChange={(e) => setCancelImmediate(e.target.checked)}
                className="mt-0.5 rounded border-gray-300"
              />
              <span className="text-sm font-medium text-text-primary">Cancel immediately</span>
            </label>
            <p className="mb-4 text-xs text-text-muted">
              {cancelImmediate
                ? 'The subscription will end now. The customer loses access immediately and will not receive their next workbook.'
                : 'The subscription will remain active until the current period ends. The customer keeps access until then but will not be charged again.'}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCancelModalSub(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:bg-gray-100"
              >
                Go Back
              </button>
              <button
                onClick={handleCancelConfirm}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                {cancelImmediate ? 'Cancel Now' : 'Cancel at Period End'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Subscription Row (with expandable orders)
// =============================================================================

interface SubscriptionRowProps {
  sub: SubscriptionWithProfile;
  isExpanded: boolean;
  orders: OrderWithDelivery[] | undefined;
  isLoadingOrders: boolean;
  canCancel: boolean;
  isCanceling: boolean;
  onToggleExpand: () => void;
  onCancel: () => void;
}

function SubscriptionRow({
  sub,
  isExpanded,
  orders,
  isLoadingOrders,
  canCancel,
  isCanceling,
  onToggleExpand,
  onCancel,
}: SubscriptionRowProps) {
  return (
    <>
      <tr
        className="cursor-pointer border-b border-border-light transition-colors last:border-b-0 hover:bg-gray-50"
        onClick={onToggleExpand}
      >
        {/* Expand chevron */}
        <td className="px-2 py-3 text-text-muted">
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>

        <td className="px-4 py-3">
          <p className="font-medium text-text-primary">{sub.profiles?.full_name ?? 'Unknown'}</p>
          <p className="text-xs text-text-muted">{sub.profiles?.email ?? ''}</p>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <SubscriptionStatusBadge status={sub.status} />
            {sub.cancel_at_period_end && (
              <span className="text-xs font-medium text-orange-600">Canceling</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 capitalize text-text-muted">{sub.pace}</td>
        <td className="px-4 py-3 text-text-muted">{formatDate(sub.current_period_end)}</td>
        <td className="px-4 py-3 text-text-muted">{formatDate(sub.created_at)}</td>
        <td className="px-4 py-3">
          {canCancel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              disabled={isCanceling}
              className="inline-flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isCanceling ? (
                <>
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Canceling…
                </>
              ) : (
                <>
                  <Ban className="h-3 w-3" />
                  Cancel
                </>
              )}
            </button>
          )}
        </td>
      </tr>

      {/* Expanded order rows */}
      {isExpanded && (
        <tr>
          <td colSpan={7} className="border-b border-border-light bg-gray-50/50 px-4 py-3">
            <ExpandedOrders orders={orders} isLoading={isLoadingOrders} />
          </td>
        </tr>
      )}
    </>
  );
}

// =============================================================================
// Expanded Orders Sub-table
// =============================================================================

interface ExpandedOrdersProps {
  orders: OrderWithDelivery[] | undefined;
  isLoading: boolean;
}

function ExpandedOrders({ orders, isLoading }: ExpandedOrdersProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        <span className="text-xs text-text-muted">Loading orders…</span>
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return <p className="py-2 text-xs text-text-muted">No orders linked to this subscription.</p>;
  }

  return (
    <div className="ml-6">
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
        Linked Orders ({orders.length})
      </p>
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="text-text-muted">
            <th className="pb-1 pr-4 font-medium">Status</th>
            <th className="pb-1 pr-4 font-medium">Delivery</th>
            <th className="pb-1 pr-4 font-medium">Amount</th>
            <th className="pb-1 pr-4 font-medium">Created</th>
            <th className="pb-1 pr-4 font-medium">Delivered</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const deliveryStatus = order.delivery_log?.[0]?.status;

            return (
              <tr key={order.id} className="border-t border-border-light">
                <td className="py-1.5 pr-4">
                  <OrderStatusBadge status={order.status} />
                </td>
                <td className="py-1.5 pr-4 capitalize text-text-muted">{order.delivery_method}</td>
                <td className="py-1.5 pr-4 text-text-primary">
                  {formatCurrency(order.amount_cents, order.currency)}
                </td>
                <td className="py-1.5 pr-4 text-text-muted">{formatDate(order.created_at)}</td>
                <td className="py-1.5 pr-4 text-text-muted">
                  {order.delivered_at ? (
                    formatDate(order.delivered_at)
                  ) : deliveryStatus ? (
                    <span className="capitalize">{deliveryStatus}</span>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Status Badges
// =============================================================================

interface SubscriptionStatusBadgeProps {
  status: SubscriptionStatus;
}

function SubscriptionStatusBadge({ status }: SubscriptionStatusBadgeProps) {
  const color = STATUS_COLORS[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${color}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

interface OrderStatusBadgeProps {
  status: OrderStatus;
}

function OrderStatusBadge({ status }: OrderStatusBadgeProps) {
  const color = ORDER_STATUS_COLORS[status];
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${color}`}
    >
      {status}
    </span>
  );
}
